import type { GameCommand, GameSnapshot } from '../core/types';

export interface SoundControl {
  isMuted(): boolean;
  toggle(): boolean;
}

function requiredElement(root: ParentNode, selector: string): Element {
  const element = root.querySelector(selector);
  if (element === null) throw new Error(`Missing UI element: ${selector}`);
  return element;
}

export class GameUi {
  public readonly canvas: HTMLCanvasElement;
  private readonly hud: HTMLElement;
  private readonly score: HTMLElement;
  private readonly coins: HTMLElement;
  private readonly distance: HTMLElement;
  private readonly ready: HTMLElement;
  private readonly countdown: HTMLElement;
  private readonly paused: HTMLElement;
  private readonly gameOver: HTMLElement;
  private readonly finalScore: HTMLElement;
  private readonly finalDistance: HTMLElement;
  private readonly finalCoins: HTMLElement;
  private readonly unsupported: HTMLElement;
  private readonly soundToggle: HTMLButtonElement;
  private readonly soundState: HTMLElement;
  private readonly shell: HTMLElement;
  private previousPhase: GameSnapshot['phase'] = 'ready';
  private impactRevealAt = 0;

  public constructor(
    root: HTMLElement,
    private readonly dispatch: (command: GameCommand) => void,
    private readonly sound: SoundControl,
  ) {
    root.innerHTML = `
      <main class="game-shell">
        <canvas class="game-canvas" data-testid="game-canvas" aria-label="Selfie Sprint game"></canvas>
        <button class="sound-toggle" data-ui="sound-toggle" data-testid="sound-toggle" type="button">
          <span>SFX</span><strong data-ui="sound-state">ON</strong>
        </button>
        <section class="hud" data-testid="hud" aria-live="polite" hidden>
          <div class="hud-pill"><span class="hud-label">Coins</span><strong data-ui="coins">0</strong></div>
          <div class="score-block"><span class="hud-label">Score</span><strong data-ui="score">0</strong></div>
          <div class="hud-pill"><span class="hud-label">Distance</span><strong data-ui="distance">0 m</strong></div>
        </section>
        <aside class="orientation-notice">Portrait works best</aside>
        <section class="screen ready-screen" data-ui="ready" data-testid="ready-screen">
          <div class="brand-mark" aria-hidden="true"><span>S</span></div>
          <p class="eyebrow">A SELFIE GAMES PROTOTYPE</p>
          <h1>Selfie<br /><em>Sprint</em></h1>
          <p class="lede">Dodge, duck, jump. Your face joins the run later.</p>
          <button class="primary-button" data-command="primary" data-testid="play-button">Play</button>
          <p class="controls-hint">Swipe to move · ↑ jump · ↓ slide</p>
        </section>
        <div class="countdown" data-ui="countdown" data-testid="countdown" aria-live="assertive" hidden>3</div>
        <section class="screen compact-screen" data-ui="paused" data-testid="pause-screen" hidden>
          <p class="eyebrow">RUN PAUSED</p>
          <h2>Ready?</h2>
          <button class="primary-button" data-command="primary">Resume</button>
        </section>
        <section class="screen compact-screen" data-ui="game-over" data-testid="game-over-screen" hidden>
          <p class="eyebrow">RUN COMPLETE</p>
          <h2>Nice sprint.</h2>
          <strong class="final-score" data-ui="final-score">0</strong>
          <div class="summary-row">
            <span><small>Distance</small><strong data-ui="final-distance">0 m</strong></span>
            <span><small>Coins</small><strong data-ui="final-coins">0</strong></span>
          </div>
          <button class="primary-button" data-command="primary" data-testid="retry-button">Run again</button>
        </section>
        <section class="screen compact-screen" data-ui="unsupported" data-testid="unsupported-screen" hidden>
          <p class="eyebrow">GRAPHICS UNAVAILABLE</p>
          <h2>WebGL 2 is required.</h2>
          <p class="lede">Try a current version of Chrome, Safari, Firefox, or Edge.</p>
        </section>
        <output class="debug-panel" data-ui="debug" aria-label="Game diagnostics"></output>
      </main>
    `;

    this.shell = requiredElement(root, '.game-shell') as HTMLElement;
    this.canvas = requiredElement(root, '[data-testid="game-canvas"]') as HTMLCanvasElement;
    this.hud = requiredElement(root, '[data-testid="hud"]') as HTMLElement;
    this.score = requiredElement(root, '[data-ui="score"]') as HTMLElement;
    this.coins = requiredElement(root, '[data-ui="coins"]') as HTMLElement;
    this.distance = requiredElement(root, '[data-ui="distance"]') as HTMLElement;
    this.ready = requiredElement(root, '[data-ui="ready"]') as HTMLElement;
    this.countdown = requiredElement(root, '[data-ui="countdown"]') as HTMLElement;
    this.paused = requiredElement(root, '[data-ui="paused"]') as HTMLElement;
    this.gameOver = requiredElement(root, '[data-ui="game-over"]') as HTMLElement;
    this.finalScore = requiredElement(root, '[data-ui="final-score"]') as HTMLElement;
    this.finalDistance = requiredElement(root, '[data-ui="final-distance"]') as HTMLElement;
    this.finalCoins = requiredElement(root, '[data-ui="final-coins"]') as HTMLElement;
    this.unsupported = requiredElement(root, '[data-ui="unsupported"]') as HTMLElement;
    this.soundToggle = requiredElement(root, '[data-ui="sound-toggle"]') as HTMLButtonElement;
    this.soundState = requiredElement(root, '[data-ui="sound-state"]') as HTMLElement;
    this.soundToggle.addEventListener('click', this.onSoundToggle);
    this.renderSoundState(this.sound.isMuted());

    for (const button of root.querySelectorAll<HTMLButtonElement>('[data-command="primary"]')) {
      button.addEventListener('click', this.onPrimary);
    }
  }

  public update(snapshot: Readonly<GameSnapshot>): void {
    const now = performance.now();
    if (this.previousPhase === 'running' && snapshot.phase === 'gameOver') {
      this.impactRevealAt = now + 420;
    }
    const holdingImpact = snapshot.phase === 'gameOver' && now < this.impactRevealAt;
    this.shell.classList.toggle('is-impacting', holdingImpact);
    this.hud.hidden = snapshot.phase === 'ready';
    this.ready.hidden = snapshot.phase !== 'ready';
    this.countdown.hidden = snapshot.phase !== 'countdown';
    this.paused.hidden = snapshot.phase !== 'paused';
    this.gameOver.hidden = snapshot.phase !== 'gameOver' || holdingImpact;

    this.score.textContent = snapshot.score.toLocaleString();
    this.coins.textContent = snapshot.coins.toString();
    this.distance.textContent = `${Math.floor(snapshot.distance)} m`;
    this.countdown.textContent = Math.max(1, Math.ceil(snapshot.countdownRemaining)).toString();
    this.finalScore.textContent = snapshot.score.toLocaleString();
    this.finalDistance.textContent = `${Math.floor(snapshot.distance)} m`;
    this.finalCoins.textContent = snapshot.coins.toString();
    this.previousPhase = snapshot.phase;
  }

  public showUnsupported(): void {
    this.unsupported.hidden = false;
    this.ready.hidden = true;
    this.hud.hidden = true;
    this.canvas.hidden = true;
    this.soundToggle.hidden = true;
  }

  public debugElement(): HTMLOutputElement {
    return requiredElement(document, '[data-ui="debug"]') as HTMLOutputElement;
  }

  private readonly onPrimary = (): void => {
    this.dispatch({ type: 'primary' });
  };

  private readonly onSoundToggle = (): void => {
    this.renderSoundState(this.sound.toggle());
  };

  private renderSoundState(muted: boolean): void {
    this.soundState.textContent = muted ? 'OFF' : 'ON';
    this.soundToggle.setAttribute('aria-pressed', String(!muted));
    this.soundToggle.setAttribute('aria-label', muted ? 'Turn sound on' : 'Turn sound off');
  }
}
